import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { schedulesVerificationService } from '@/services/schedulesVerification.service';
import { isSoftVerificationFailure } from '@/utils/scheduleVerification';
import {
  isTransitionalSummary,
  startVerificationSession,
} from '@/utils/verificationSession';
import type { ScheduleVerificationResponse, VerificationStatus } from '@/types/api';
import type { ScheduleVerificationAvailability } from './useScheduleVerification';

export interface ControllerVerificationSnapshot {
  controllerId: string;
  unavailable: boolean;
  summary?: ScheduleVerificationResponse['summary'];
  worstStatus?: VerificationStatus | null;
  isLoading: boolean;
  isVerifying: boolean;
}

function worstStatusFromSummary(
  summary: ScheduleVerificationResponse['summary']
): VerificationStatus | null {
  if (summary.error > 0) return 'error';
  if (summary.missing > 0) return 'missing';
  if (summary.mismatch > 0) return 'mismatch';
  if (summary.pending > 0) return 'pending';
  if (summary.sent > 0) return 'sent';
  if (summary.confirmed > 0) return 'confirmed';
  if (summary.verified > 0) return 'verified';
  return null;
}

export function useControllersVerificationStatuses(
  controllerIds: string[],
  enabled = true
) {
  const queryClient = useQueryClient();

  const queries = useQueries({
    queries: controllerIds.map((controllerId) => ({
      queryKey: ['schedule-verification', controllerId] as const,
      queryFn: async (): Promise<ScheduleVerificationAvailability> => {
        const res = await schedulesVerificationService.getStatus(controllerId);
        if (!res.success || !res.data) {
          const code = res.error?.code;
          const message =
            res.error?.message || 'Verificación no disponible';
          if (isSoftVerificationFailure(code, message)) {
            return { status: 'unavailable', message };
          }
          throw new Error(message);
        }
        return { status: 'ready', data: res.data };
      },
      enabled: enabled && !!controllerId,
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 60_000,
    })),
  });

  const byId = new Map<string, ControllerVerificationSnapshot>();
  controllerIds.forEach((id, index) => {
    const q = queries[index];
    const data = q?.data;
    if (data?.status === 'ready') {
      byId.set(id, {
        controllerId: id,
        unavailable: false,
        summary: data.data.summary,
        worstStatus: worstStatusFromSummary(data.data.summary),
        isLoading: q.isLoading,
        isVerifying: false,
      });
    } else if (data?.status === 'unavailable') {
      byId.set(id, {
        controllerId: id,
        unavailable: true,
        isLoading: q.isLoading,
        isVerifying: false,
      });
    } else {
      byId.set(id, {
        controllerId: id,
        unavailable: false,
        isLoading: q?.isLoading ?? false,
        isVerifying: false,
      });
    }
  });

  const verifyController = useCallback(
    async (controllerId: string, force = true) => {
      const res = await schedulesVerificationService.verify(controllerId, force);
      if (!res.success || !res.data) {
        const code = res.error?.code;
        const message = res.error?.message || 'Error al verificar';
        if (isSoftVerificationFailure(code, message)) {
          throw new Error(
            'Verificación no disponible. Compruebe que el backend v0.7 esté desplegado.'
          );
        }
        throw new Error(message);
      }
      queryClient.setQueryData(['schedule-verification', controllerId], {
        status: 'ready',
        data: res.data,
      } satisfies NonNullable<ScheduleVerificationAvailability>);

      if (isTransitionalSummary(res.data.summary)) {
        startVerificationSession({
          controllerId,
          controllerName: res.data.controllerName,
          total: res.data.summary.total || res.data.schedules.length || 1,
          summary: res.data.summary,
          isLora: true,
        });
      }

      await queryClient.invalidateQueries({
        queryKey: ['schedules-verification-summary'],
      });
      return res.data;
    },
    [queryClient]
  );

  return { byId, verifyController, queries };
}
